import React, { useEffect, useRef, useState } from 'react';
import { useAI } from '../../context/AIContext';
import { typography, surfaces, borders, textColors, interactive } from '../../../design-system/tokens';
import { ArrowRightIcon, CheckmarkIcon, ClipboardIcon, CloseIcon, GearIcon, HistoryIcon, SendIcon } from './icons/AIIcons';
import useChatAssetPins from './hooks/useChatAssetPins';
import AIAssetLibraryModal from './AIAssetLibraryModal';

const ACTION_BUTTON_BASE_CLASS = 'w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-xs font-medium leading-5 transition-colors';
const ACTION_BUTTON_DISABLED_CLASS = 'opacity-60 cursor-not-allowed';
const REVIEW_PREPARE_PLAN_MESSAGE = 'Please use what you have so far and prepare the website changes for approval.';
const REVIEW_NEXT_IMPROVEMENT_MESSAGE = 'Go to next improvement suggestion.';
const REVIEW_NEXT_IMPROVEMENT_LABEL = 'Go to next improvement suggestion';
const REVIEW_POST_APPLY_FOLLOWUP_MESSAGE = 'Please adjust the changes you just applied in these ways: ';

const AIReviewMessageActions = ({ chatId, message, onPrefillDraft, onFocusComposer, isLatestMessage = true }) => {
  const { currentChat, sendReviewAction, updatePinnedAssets } = useAI();
  const [submittingAction, setSubmittingAction] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileInputRef = useRef(null);
  const context = message?.context || {};
  const isApprovalMessage = context?.message_type === 'review_approval';
  const isConsultationMessage = context?.message_type === 'review_consultation';
  const isPostApplyMessage = context?.message_type === 'review_post_apply';
  const isReviewActionMessage = isApprovalMessage || isConsultationMessage || isPostApplyMessage;
  const uploadRequest = context?.upload_request || null;
  const implementationSummary = Array.isArray(context?.implementation_summary) ? context.implementation_summary : [];
  const {
    assetError,
    canAddMoreAssets,
    pendingPins,
    pinnedAssetIds,
    setAssetError,
    setPendingPins,
    setPinnedAssetIds,
    uploadAndPin,
  } = useChatAssetPins({
    chatId,
    pinnedAssets: currentChat?.pinned_assets || [],
    updatePinnedAssets,
  });

  const isBusy = Boolean(submittingAction) || pendingPins.length > 0;
  const areActionsInactive = !isLatestMessage;
  const areButtonsDisabled = areActionsInactive || isBusy;

  useEffect(() => {
    if (areActionsInactive) {
      setLibraryOpen(false);
    }
  }, [areActionsInactive]);

  if (!isReviewActionMessage) {
    return null;
  }

  const sendAssetFollowupMessage = async (asset) => {
    if (!asset || areActionsInactive) return;
    await sendReviewAction(
      chatId,
      'review_prepare_plan',
      {
        content: `I uploaded ${asset.original_filename || 'a file'}. Please use it to prepare the website changes and ask for approval if you have enough information.`,
      }
    );
  };

  const handleReviewAction = async (actionName, fallbackContent) => {
    if (!chatId || areButtonsDisabled) return;
    try {
      setSubmittingAction(actionName);
      await sendReviewAction(chatId, actionName, { content: fallbackContent });
    } finally {
      setSubmittingAction('');
    }
  };

  const handleUploadSelected = async (event) => {
    const file = (event.target.files || [])[0] || null;
    event.target.value = '';
    if (!file || !chatId || areButtonsDisabled) return;
    const completedAsset = await uploadAndPin(file);
    if (!completedAsset) return;
    await sendAssetFollowupMessage(completedAsset);
  };

  const handleWriteAnswer = () => {
    if (areButtonsDisabled) return;
    if (typeof onFocusComposer === 'function') {
      onFocusComposer();
      return;
    }
    onPrefillDraft?.('');
  };

  const handleWriteFollowup = () => {
    if (areButtonsDisabled) return;
    if (typeof onPrefillDraft === 'function') {
      onPrefillDraft(REVIEW_POST_APPLY_FOLLOWUP_MESSAGE);
      return;
    }
    onFocusComposer?.();
  };

  const getPrimaryButtonClassName = (disabled) => (
    `${ACTION_BUTTON_BASE_CLASS} ${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText} ${
      disabled ? ACTION_BUTTON_DISABLED_CLASS : ''
    }`
  );

  const getSecondaryButtonClassName = (disabled) => (
    `${ACTION_BUTTON_BASE_CLASS} border ${borders.default} ${surfaces.secondary} ${textColors.primary} ${
      disabled ? ACTION_BUTTON_DISABLED_CLASS : ''
    }`
  );

  return (
    <div
      data-testid="ai-review-message-actions"
      data-review-actions-active={areActionsInactive ? 'false' : 'true'}
      className="mt-3 rounded-lg border border-green-400/25 bg-green-400/10 p-3 backdrop-blur-sm"
    >
      {isApprovalMessage && implementationSummary.length > 0 && (
        <div className="space-y-2">
          <p className={`${typography.caption} ${textColors.brightest}`}>Planned changes</p>
          <ul className={`space-y-1 ${typography.caption} ${textColors.brightest} list-disc pl-4`}>
            {implementationSummary.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {isConsultationMessage && (
        <div className="space-y-2">
          {uploadRequest ? (
            <>
              <p
                data-testid="ai-review-upload-request-title"
                className={`${typography.caption} ${textColors.brightest}`}
              >
                {uploadRequest.title}
              </p>
            </>
          ) : null}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={handleWriteAnswer}
              disabled={areButtonsDisabled}
              className={getPrimaryButtonClassName(areButtonsDisabled)}
            >
              <SendIcon className="w-4 h-4 flex-shrink-0" />
              Write your answer
            </button>
            <button
              type="button"
              onClick={() => handleReviewAction('review_prepare_plan', REVIEW_PREPARE_PLAN_MESSAGE)}
              disabled={areButtonsDisabled}
              className={getPrimaryButtonClassName(areButtonsDisabled)}
            >
              <CheckmarkIcon className="w-4 h-4 flex-shrink-0" />
              Proceed with changes
            </button>
            {uploadRequest ? (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click?.()}
                  disabled={!canAddMoreAssets || areButtonsDisabled}
                  className={getPrimaryButtonClassName(!canAddMoreAssets || areButtonsDisabled)}
                >
                  <ClipboardIcon className="w-4 h-4 flex-shrink-0" />
                  {pendingPins.length > 0 ? 'Uploading...' : 'Upload file'}
                </button>
                <button
                  type="button"
                  onClick={() => setLibraryOpen(true)}
                  disabled={!canAddMoreAssets || areButtonsDisabled}
                  className={getPrimaryButtonClassName(!canAddMoreAssets || areButtonsDisabled)}
                >
                  <HistoryIcon className="w-4 h-4 flex-shrink-0" />
                  Choose from uploaded
                </button>
              </>
            ) : null}
            <button
              type="button"
              onClick={() => handleReviewAction('review_skip', REVIEW_NEXT_IMPROVEMENT_MESSAGE)}
              disabled={areButtonsDisabled}
              className={getSecondaryButtonClassName(areButtonsDisabled)}
            >
              <ArrowRightIcon className="w-4 h-4 flex-shrink-0" />
              {REVIEW_NEXT_IMPROVEMENT_LABEL}
            </button>
          </div>
        </div>
      )}

      {isApprovalMessage && (
        <div className="mt-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => handleReviewAction('review_approve', 'Yes, apply it.')}
            disabled={areButtonsDisabled}
            className={getPrimaryButtonClassName(areButtonsDisabled)}
          >
            <CheckmarkIcon className="w-4 h-4 flex-shrink-0" />
            Yes
          </button>
          <button
            type="button"
            onClick={() => {
              if (areButtonsDisabled) return;
              onPrefillDraft?.('I want to change this implementation before you apply it. Please adjust it in these ways: ');
            }}
            disabled={areButtonsDisabled}
            className={getPrimaryButtonClassName(areButtonsDisabled)}
          >
            <CloseIcon className="w-4 h-4 flex-shrink-0" />
            No
          </button>
          <button
            type="button"
            onClick={() => handleReviewAction('review_skip', REVIEW_NEXT_IMPROVEMENT_MESSAGE)}
            disabled={areButtonsDisabled}
            className={getSecondaryButtonClassName(areButtonsDisabled)}
          >
            <ArrowRightIcon className="w-4 h-4 flex-shrink-0" />
            {REVIEW_NEXT_IMPROVEMENT_LABEL}
          </button>
        </div>
      )}

      {isPostApplyMessage && (
        <div className="mt-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleWriteFollowup}
            disabled={areButtonsDisabled}
            className={getPrimaryButtonClassName(areButtonsDisabled)}
          >
            <SendIcon className="w-4 h-4 flex-shrink-0" />
            Write a message
          </button>
          <button
            type="button"
            onClick={() => handleReviewAction('review_next_improvement', REVIEW_NEXT_IMPROVEMENT_MESSAGE)}
            disabled={areButtonsDisabled}
            className={getSecondaryButtonClassName(areButtonsDisabled)}
          >
            <ArrowRightIcon className="w-4 h-4 flex-shrink-0" />
            {REVIEW_NEXT_IMPROVEMENT_LABEL}
          </button>
        </div>
      )}

      {(submittingAction || pendingPins.length > 0) && !areActionsInactive && (
        <div
          data-testid="ai-review-actions-status"
          className={`mt-3 flex items-center gap-2 ${typography.caption} ${textColors.brightest}`}
        >
          <GearIcon className="w-3.5 h-3.5 animate-spin" />
          <span>{submittingAction ? 'Sending your review action...' : 'Uploading your file...'}</span>
        </div>
      )}

      {assetError?.message ? (
        <p className={`mt-3 ${typography.caption} text-red-500 dark:text-red-400`}>{assetError.message}</p>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleUploadSelected}
      />
      <AIAssetLibraryModal
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        disabledAssetIds={pinnedAssetIds}
        maxSelectable={5}
        onSelect={async (asset) => {
          if (!asset?.id || !canAddMoreAssets || areButtonsDisabled) return;
          const key = `pending-lib-${asset.id}`;
          setAssetError(null);
          setPendingPins((prev) => [
            ...prev,
            { key, filename: asset.original_filename || `Asset #${asset.id}`, stage: 'pinning', assetId: asset.id },
          ]);
          try {
            const ok = await setPinnedAssetIds([...pinnedAssetIds, asset.id]);
            if (!ok) return;
            setLibraryOpen(false);
            await sendAssetFollowupMessage(asset);
          } finally {
            setPendingPins((prev) => prev.filter((pin) => pin.key !== key));
          }
        }}
      />
    </div>
  );
};

export default AIReviewMessageActions;
